/**
 * 素材庫的共用常數。
 *
 * ⚠ **後端才是權威。** 這裡的每個數字都是為了「送出前先擋一次、省一趟上傳」，
 * 不是驗證的來源。後端 `app/Domain/Constant/MediaConstants.ts` 改了要跟著改，
 * 但**兩邊不同步的後果只是使用者多等一次來回**，不會寫壞資料。
 */

/** 依 `kind` 的檔案大小上限（與後端 MediaConstants 一致）。 */
export const MAX_MB = {
    image: 10,
    pdf: 20,
    /**
     * ⚠ 影片真正的保護是**長度上限 10 分鐘**（後端用 ffprobe 擋，ADR 0011），
     * 不是位元組。500MB 只是為了不讓一支超大檔佔滿暫存空間——
     * 同樣長度的影片在不同 bitrate 下轉檔成本差十幾倍。
     */
    video: 500,
};

export const MAX_BYTES = {
    image: MAX_MB.image * 1024 * 1024,
    pdf: MAX_MB.pdf * 1024 * 1024,
    video: MAX_MB.video * 1024 * 1024,
};

/** 影片長度上限（分鐘）。前端只用來顯示說明，實際檢查在後端。 */
export const VIDEO_MAX_MINUTES = 10;

/**
 * 影片長邊的上限（＝1080p）。與後端 `MediaConstants.VIDEO_MAX_LONG_EDGE` 一致。
 *
 * ⚠ 比的是 `Math.max(width, height)` 而不是 width——直式的 width 很小但 height
 * 很大，只看 width 會讓 1920×2560 鑽過去。
 */
export const VIDEO_MAX_LONG_EDGE = 1920;

/**
 * 影片幀率的上限。與後端 `MediaConstants.VIDEO_MAX_FPS` 一致。
 *
 * ⚠⚠ **前端讀不到幀率**（`<video>` 的 metadata 沒有這個值），所以這個數字在前端
 * 只出現在提示文字裡、擋不了任何東西。60fps 只會在後端被擋——**這也是事前提示
 * 特別重要的原因**：那是老師唯一能提前知道這條限制的地方。
 */
export const VIDEO_MAX_FPS = 30;

/** 每人的總容量配額（GB）。 */
export const USER_QUOTA_GB = 2;

/**
 * `<input type="file" accept>` 的值。
 * ⚠ HEIC 是 iPhone 的預設格式，老師很可能直接丟原檔，所以要收。
 */
export const ACCEPT = {
    image: 'image/jpeg,image/png,image/webp,image/heic,image/heif',
    pdf: 'application/pdf',
    video: 'video/mp4,video/quicktime',
};

/**
 * 副檔名白名單（與後端 `*_EXTNAMES` 一致）。
 * ⚠ 必須用它而不是只看 `file.type`：**HEIC 在某些瀏覽器的 `file.type` 是空字串**，
 * 只看 MIME 會把 iPhone 的照片誤判成不支援。
 */
export const EXTNAMES = {
    image: ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'],
    pdf: ['pdf'],
    video: ['mp4', 'mov'],
};

/**
 * `purpose` → `kind` 的映射（與後端 `DiaryMediaDef.PURPOSE_TO_KIND` 一致）。
 * ⚠ 單向：`purpose` 推得出 `kind`，反過來不行（`image` 可能是 cover / image / hotspot）。
 */
export const PURPOSE_TO_KIND = {
    cover: 'image',
    image: 'image',
    hotspot: 'image',
    pdf: 'pdf',
    video: 'video',
    wordsearch: 'wordlist',
    colorword: 'wordlist',
    audio: 'audio',
};

/**
 * 這些 `purpose` **不能上傳**——字表沒有檔案，內容在 `attributes.words`（規格 §2.2）。
 * 後端 `POST /diary/media` 收到 wordlist 會明確回 400，所以 UI 不該給上傳入口。
 *
 * ⚠ **「不能上傳」不等於「不能建立」。** 字表走 `POST /diary/media/wordlist`
 * （JSON，不是 multipart），入口是「建立字表」而不是「上傳新的」——見 `isCreatable`。
 */
export const UPLOADABLE_KINDS = ['image', 'pdf', 'video'];

/**
 * 這些 `kind` **老師完全不能自己建立**——唯一的產生者是系統。
 *
 * ⚠ `audio` 是互動影片問答的語音，由 TTS worker 依老師打的文字生成
 * （VideoQuest.console.steps.md 的 V8）。**不要給它上傳入口也不要給建立入口**：
 * 開放等於多一條沒人要求、後端也會回 400 的路徑
 * （`DiaryMediaUploadService` 只收 image / pdf / video）。
 */
export const SYSTEM_ONLY_KINDS = ['audio'];

/**
 * 字表的內容規則（與後端 `WordlistValidator` 一致）。
 *
 * ⚠ **兩個 purpose 的規則不一樣，不能共用一套。** `wordsearch` 與 `colorword` 的
 * `kind` 都是 `wordlist`，但學生端吃它們的方式完全不同——找單字的字要被**藏進字母格**，
 * 講顏色的字只是**顯示用的**。三個限制都是從學生端元件讀出來的硬限制，而且
 * **違反的後果全都是靜默的**（不報錯、不過關），所以 UI 一定要在送出前就講清楚。
 */
export const WORDLIST = {
    MAX_WORDS: 60,
    /** 講顏色的字長上限（純顯示用）。 */
    MAX_WORD_LENGTH: 24,
    /**
     * 找單字的最短字長。
     * ⚠ **不是 1**：`WordSearch.vue` 的 `endDrag()` 有 `if (path.length < 2) return`，
     * 所以單字母的字**永遠選不起來**——學生怎麼拖都不會判定為找到。
     */
    MIN_WORDSEARCH_LENGTH: 2,
    /**
     * 找單字的最長字長 = `grid_size` 的上限。
     * ⚠ 比格子長的字**靜默地放不進格子**（`generateGame()` 試 100 次就放棄且不報錯），
     * 學生找不到、`target_count` 可能永遠達不到。
     */
    MAX_WORDSEARCH_LENGTH: 15,
    /** 找單字的合法字元。⚠ 格子的填充字元是大寫 A–Z，其他字元一眼就看出答案在哪。 */
    WORDSEARCH_PATTERN: /^[A-Z]+$/,
};

/// ⚠ 這裡曾經有一份 `COLOR_WORDS`（`Color.vue` 那 9 個顏色），給字表對話框的
/// 「填入 9 個顏色詞」快速鍵用。那顆按鈕 2026-09-01 移除了，所以這個常數也一起拿掉。
///
/// ⚠ **那 9 個顏色的紀錄沒有消失**，它搬到唯一還需要它的兩個地方：
/// 後端 `WordlistValidator.COLOR_WORDS`（連同一項驗證，當 `Color.vue` 改動時的絆線），
/// 以及 `media.wordlist.words_rule_colorword` 的三語文案（那是老師真的會看到的地方）。
/// **不要在這裡重新加回來**——前端已經沒有任何程式需要它。

/**
 * 找不同的熱區最小邊長，**以圖寬／圖高的比例表示**。
 * ⚠ 與後端 `MediaConstants.HOTSPOT_MIN_EDGE_RATIO` 同一個數字——改一邊要改兩邊。
 * ⚠ 太小的框學生**點不到**，而那是靜默的死路：完成判定寫在「點中了」的分支裡，
 * 所以永遠不會過關、也不報錯。
 */
export const HOTSPOT_MIN_EDGE_RATIO = 0.05;

/** `diary_media.share_scope` 的值域。⚠ **沒有 `team`**——team 共用已於 2026-08-20 取消。 */
export const SHARE_SCOPES = ['private', 'public'];

/**
 * 素材的狀態值（`diary_media.status`）。
 *
 * `delisted` 是**擁有者自己**下架的，他可以一鍵上架回來。
 * `banned` 是**平台**下架的，擁有者看得到、看得到原因，但**不能自己上架**——
 * 後端的 `relist` 會回 403。原因在 `attributes.ban_reason`。
 */
export const STATUS_ACTIVE = 'active';
export const STATUS_DELISTED = 'delisted';
export const STATUS_BANNED = 'banned';

/**
 * 這份素材是不是「挑不到、而且用到它的題目會被停用」。
 *
 * ⚠⚠ **判斷「素材不能用」一律用這個，不要寫 `media.status === 'delisted'`。**
 * `banned` 加進來之前有六處寫死比對 `delisted`，每一處漏掉就是一個洞：
 * 存得起用到它的題目、素材欄不標記、或是把它算成「已經沒問題」。
 * 後端有一支同名同義的 `DiaryMediaDef.isBlockingStatus`——兩邊要一起改。
 */
export function isBlocked(media) {
    if (!media) {
        return false;
    }
    return media.status === STATUS_DELISTED || media.status === STATUS_BANNED;
}

/** 是不是被平台下架（不是擁有者自己下架的）。決定要不要給「重新上架」。 */
export function isBanned(media) {
    return !!media && media.status === STATUS_BANNED;
}

/**
 * 平台下架的原因。沒有就回空字串——文案要能在沒有原因時也讀得通。
 *
 * ⚠⚠ **只有素材的擁有者能看到原因。** 那是平台與上傳者之間的事，可能寫著版權來源、
 * 檢舉內容或那位老師的行為；別的老師只需要知道「這份素材不能用了，換一個」。
 *
 * ⚠ 後端已經在三個回傳點把 `ban_reason` 從 `attributes` 剝掉了
 * （`DiaryMediaService.stripOwnerOnlyAttrs`），所以非擁有者根本拿不到這個值。
 * 這裡再擋一次不是不信後端，而是**這個函式可能被新的呼叫端用在別的資料來源上**
 * ——例如某個還沒剝乾淨的端點。兩層都擋，漏一層還有另一層。
 */
export function banReasonOf(media) {
    if (!media || media.is_mine === false) {
        return '';
    }
    const reason = attrsOf(media).ban_reason;
    return typeof reason === 'string' ? reason : '';
}

export function kindOfPurpose(purpose) {
    return PURPOSE_TO_KIND[purpose] || null;
}

export function isUploadable(purpose) {
    return UPLOADABLE_KINDS.indexOf(kindOfPurpose(purpose)) !== -1;
}

/**
 * 這個 `purpose` 是不是「在介面上建立」的（而不是上傳的）。
 *
 * ⚠ 目前只有字表。它與 `isUploadable` 是**互斥**的：有檔案的素材上傳、
 * 沒檔案的素材建立，沒有第三種。分成兩個函式而不是一個三值的，是因為呼叫端
 * 要的是「該顯示哪一顆按鈕」，而那兩顆按鈕的行為完全不同。
 */
export function isCreatable(purpose) {
    return kindOfPurpose(purpose) === 'wordlist';
}

/** `attributes.words`，讀不出來回空陣列。⚠ 走 `attrsOf` 才擋得住 LONGTEXT 回字串。 */
export function wordsOf(media) {
    const words = attrsOf(media).words;
    return Array.isArray(words) ? words : [];
}

/**
 * 前端的字表檢查，回傳**錯誤訊息的 i18n key + 參數**，沒問題回 null。
 *
 * ⚠ **後端才是權威**（`WordlistValidator`）。這裡存在的理由是「別讓老師打完 20 個字
 * 才被一個英文技術訊息退回」——後端的訊息是給開發者看的，不適合直接顯示給老師。
 *
 * @param {string[]} words 已經 trim 過的單字
 * @param {string} purpose `wordsearch` / `colorword`
 * @returns {{key: string, params: Object}|null}
 */
export function checkWords(words, purpose) {
    if (!words.length) {
        return { key: 'media.wordlist.err_empty', params: {} };
    }
    if (words.length > WORDLIST.MAX_WORDS) {
        return { key: 'media.wordlist.err_too_many', params: { n: WORDLIST.MAX_WORDS } };
    }
    if (purpose !== 'wordsearch') {
        const long = words.find((w) => w.length > WORDLIST.MAX_WORD_LENGTH);
        return long
            ? { key: 'media.wordlist.err_too_long', params: { w: long, n: WORDLIST.MAX_WORD_LENGTH } }
            : null;
    }
    /// 以下都是找單字的硬限制，順序照「老師最可能犯」排：字元集 → 太長 → 太短。
    const bad = words.find((w) => !WORDLIST.WORDSEARCH_PATTERN.test(w.toUpperCase()));
    if (bad) {
        return { key: 'media.wordlist.err_not_letters', params: { w: bad } };
    }
    const long = words.find((w) => w.length > WORDLIST.MAX_WORDSEARCH_LENGTH);
    if (long) {
        return {
            key: 'media.wordlist.err_ws_too_long',
            params: { w: long, n: WORDLIST.MAX_WORDSEARCH_LENGTH },
        };
    }
    const short = words.find((w) => w.length < WORDLIST.MIN_WORDSEARCH_LENGTH);
    if (short) {
        return {
            key: 'media.wordlist.err_ws_too_short',
            params: { w: short, n: WORDLIST.MIN_WORDSEARCH_LENGTH },
        };
    }
    return null;
}

/**
 * 把使用者輸入的一整段文字切成單字陣列。**與後端的分隔符一致。**
 *
 * ⚠ **空白不是分隔符**：講顏色的字表可以有含空白的詞（`light blue`）。
 * ⚠ `\s` **已經涵蓋全形空白**（U+3000），不必額外列出來——列了反而會被
 * `no-irregular-whitespace` 擋下（原始碼裡不能有全形空白）。
 * ⚠ 找單字**不在這裡轉大寫**——那會讓輸入框在打字時跳動。轉大寫交給後端
 * （它一律 `toUpperCase()`），前端只在驗證時忽略大小寫。
 */
export function splitWords(text) {
    return String(text || '')
        .split(/[\n\r,;、，；]+/)
        .map((w) => w.replace(/\s+/g, ' ').trim())
        .filter((w) => w !== '');
}

/** 副檔名（小寫、不含點）。取不到回空字串。 */
export function extOf(fileName) {
    const m = /\.([a-z0-9]+)$/i.exec(String(fileName || ''));
    return m ? m[1].toLowerCase() : '';
}

/**
 * `attributes` 的容錯讀取。
 *
 * ⚠ **MariaDB 的 JSON 實體型別是 LONGTEXT**，driver 有可能回字串而不是物件
 * （規格 §2.1）。當成物件用會靜默拿到 undefined——熱區不畫、字表是空的，
 * 而且沒有任何錯誤訊息可查。
 */
export function attrsOf(media) {
    const a = media && media.attributes;
    if (!a) return {};
    if (typeof a === 'string') {
        try {
            return JSON.parse(a) || {};
        } catch (err) {
            return {};
        }
    }
    return a;
}

/**
 * 影片的來源：`youtube`（貼連結）或 `s3`（自行上傳）。不是影片就回空字串。
 *
 * ⚠ 兩者對老師的意義不同：YouTube 影片不佔容量配額、不用等轉檔、但依賴 YouTube
 * 還在線上；自行上傳的相反。所以清單上要標出來，不能讓他只憑「有沒有縮圖」猜
 * （那是巧合——目前只有上傳的影片會抽縮圖）。
 */
/**
 * 影片來源標籤的 i18n key（接在 `media.provider.` 後面）。不是影片就回空字串。
 *
 * ⚠ **來源不只看 `provider`，還要看是誰的素材。** `provider = 's3'` 只說明「是上傳的
 * 檔案」，而同一個值在三種情境下該講三句不同的話：
 *
 * | 情境 | 標籤 |
 * |---|---|
 * | `provider = 'youtube'` | `youtube` — 誰登錄的都一樣，來源就是 YouTube |
 * | `owner_type = 'official'` | `official` 官方提供 |
 * | 自己上傳的 | `s3` 自行上傳 |
 * | 別的老師公開的 | `other` 他人上傳 |
 *
 * 2026-09-08 之前一律讀成「自行上傳」，所以挑選器的「官方」與「公開」兩個分頁裡，
 * 別人的影片與官方影片都被講成是老師自己上傳的。
 *
 * ⚠ **`owner_type` 與 `is_mine` 兩個都要有才判斷得出來。** 只有 `is_mine` 的話
 * `false` 有兩種意思（官方／別人），官方影片會被講成「他人上傳」。
 * `QuestMediaBrief` 的 `owner_type` 就是為此補上的。
 * ⚠ 兩個欄位都拿不到時回 `uploaded`（中性的「上傳的影片」）而不是猜一個——
 * 講錯「這是誰上傳的」比少講一句糟。
 */
export function providerLabelKey(media) {
    if (!media || media.kind !== 'video') {
        return '';
    }
    /// ⚠⚠ **不能用 `providerOf(media) === ''` 當「不知道」而不顯示標籤。**
    /// 手動 insert 的官方影片的 `attributes` 是 `JSON_OBJECT()`，根本沒有 `provider`
    /// ——那樣會讓「官方」分頁裡的影片一個標籤都沒有，而後端的篩選卻把它們算成
    /// 「上傳的影片」（`uploaded` = 不是 YouTube，含缺值）。兩邊必須是同一條規則。
    /// 所以這裡只把 `provider === 'youtube'` 當 YouTube，其餘一律走下面的擁有者分支。
    if (attrsOf(media).provider === 'youtube') {
        return 'youtube';
    }
    if (media.owner_type === 'official') {
        return 'official';
    }
    if (media.is_mine === true) {
        return 's3';
    }
    if (media.is_mine === false) {
        return 'other';
    }
    return 'uploaded';
}

export function providerOf(media) {
    if (!media || media.kind !== 'video') {
        return '';
    }
    return attrsOf(media).provider || '';
}
